'use client';

import { Combobox, ComboboxInput, ComboboxOption } from '@headlessui/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { twc } from 'react-twc';

import { getNaverSearch, NaverSearchItem } from '@/apis/naver.api';
import { TrackedComboboxOptions } from '@/components/headless/TrackedOverlays';
import { ScrollArea } from '@/components/ScrollArea';
import Button from '@/components/v2/Button';
import Image from '@/components/v2/Image';
import { CLIENT_URL } from '@/consts/currentUrl';
import useAlert from '@/hooks/useAlert';
import useConfirm from '@/hooks/useConfirm';
import { useCopy } from '@/hooks/useCopy';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useReferralCode } from '@/hooks/useReferral';
import { cleanupHtmlTags } from '@/utils/cleanupHtmlTags';
import { isValidOpenDate, parseOpenDate } from '@/utils/parse-input';
import { cn } from '@/utils/twx';

import { useFortuneFormData, useFortuneStep } from './useFortune';

interface FortuneSendForm {
  shopName: string;
  shopAddress: string;
  shopCategory: string;
  openedAt: string;
  shopNameSearch: string;
}

const FortuneForm = () => {
  const [, { goToNextStep }] = useFortuneStep();
  const { data: referralCode } = useReferralCode();
  const { register, handleSubmit, watch, setValue } = useForm<FortuneSendForm>({
    defaultValues: {
      shopName: '',
      shopAddress: '',
      openedAt: '',
      shopCategory: '',
      shopNameSearch: '',
    },
    mode: 'onSubmit',
    shouldFocusError: true,
  });
  const [keyword, setKeyword] = useState('');
  const [keywordIsError, setKeywordIsError] = useState(false);
  const [keywordErrorMessage, setKeywordErrorMessage] = useState('');
  const [openDateIsError, setOpenDateIsError] = useState(false);
  const [openDateErrorMessage, setOpenDateErrorMessage] = useState('');

  const keywordRef = useRef<HTMLInputElement>(null);
  const openDateRef = useRef<HTMLInputElement>(null);

  const [searchResult, setSearchResult] = useState<NaverSearchItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<NaverSearchItem | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  const [fortuneSubmittedData, setFortuneFormData] = useFortuneFormData();
  const showConfirm = useConfirm();

  const watchAllFields = watch();
  const showAlert = useAlert();

  const [, copy] = useCopy();

  const { onClickShareFortune } = useKakaoShare();

  useEffect(() => {
    if (fortuneSubmittedData.shopName) {
      showConfirm(
        '이전에 입력한 정보가 있습니다. 이어서 진행하시겠습니까?',
        () => {
          const parsedOpenDate = parseOpenDate(fortuneSubmittedData.openedAt);
          setKeyword(fortuneSubmittedData.shopName);
          setValue('shopName', fortuneSubmittedData.shopName);
          setValue('shopAddress', fortuneSubmittedData.shopAddress);
          setValue('shopCategory', fortuneSubmittedData.category);
          setValue('openedAt', parsedOpenDate);

          setSelectedItem({
            title: fortuneSubmittedData.shopName,
            roadAddress: fortuneSubmittedData.shopAddress,
            category: fortuneSubmittedData.category,
            address: fortuneSubmittedData.shopAddress,
          });
        },
        {
          confirmText: '이어서 진행하기',
          cancelText: '취소',
          subTitle: '취소를 선택하시면 이전에 입력한 정보가 삭제됩니다',
          onClickCancel: () => {
            setFortuneFormData({
              shopName: '',
              shopAddress: '',
              openedAt: '',
              category: '',
            });
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (keyword !== debouncedKeyword) {
        setDebouncedKeyword(keyword);
      }
    }, 300); // 300ms 후에 검색 실행

    return () => {
      clearTimeout(handler); // cleanup
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  // debouncedKeyword가 변경될 때마다 검색 실행
  useEffect(() => {
    if (selectedItem && cleanupHtmlTags(selectedItem.title) === keyword) return;
    if (debouncedKeyword.length >= 2) {
      const search = async () => {
        const result = await getNaverSearch(debouncedKeyword);
        setSearchResult(result.items || []);

        if (result.items.length === 0) {
          setNotFound(true);
        } else {
          setNotFound(false);
        }
      };
      search();
      return;
    }

    if (debouncedKeyword.length === 0) {
      setSearchResult([]);
      setNotFound(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);

  const handleChangeSelectItem = (item: NaverSearchItem) => {
    if (!item) {
      return;
    }

    if (selectedItem === item) {
      return;
    }
    setSelectedItem(item);

    setKeyword(cleanupHtmlTags(item.title));

    setValue('shopName', cleanupHtmlTags(item.title));
    setValue('shopAddress', cleanupHtmlTags(item.roadAddress));
    setValue('shopCategory', item.category);
    keywordRef.current?.blur();
    setTimeout(() => {
      openDateRef.current?.focus();
    }, 100);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (keywordIsError) {
      setKeywordIsError(false);
      setKeywordErrorMessage('');
    }
    setValue('shopName', '');
    setValue('shopAddress', '');
    setValue('shopCategory', '');
    setKeyword(e.target.value);
  };

  const handleChangeOpenDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (openDateIsError) {
      setOpenDateIsError(false);
      setOpenDateErrorMessage('');
    }
    const { value } = e.target;
    const formattedOpenDate = parseOpenDate(value);
    setValue('openedAt', formattedOpenDate);

    e.target.value = formattedOpenDate ?? '';
  };

  const handleBlur = () => {
    if (!keyword) return;
    if (watchAllFields.shopName === '' || watchAllFields.shopAddress === '') {
      setKeywordIsError(true);
      setKeywordErrorMessage('매장명을 검색하고 선택해주세요');
    }
  };

  const onSubmit = (data: FortuneSendForm) => {
    if ((data.shopName === '' || data.shopAddress === '') && data.shopName) {
      keywordRef.current?.focus();
      setKeywordErrorMessage('매장명을 검색하고 선택해주세요');
      setKeywordIsError(true);

      return;
    }

    if (!isValidOpenDate(data.openedAt)) {
      setOpenDateIsError(true);
      setOpenDateErrorMessage('올바른 날짜를 입력해주세요 (ex. 2000/12/20)');

      openDateRef.current?.focus();
      return;
    }

    setFortuneFormData({
      shopName: data.shopName,
      shopAddress: data.shopAddress,
      openedAt: data.openedAt,
      category: selectedItem?.category || '',
    });

    goToNextStep();
  };

  const handleClickCopy = () => {
    const addUrl = referralCode?.referral_link ? '/' + referralCode?.referral_link : '';

    copy(CLIENT_URL + '/fortune' + addUrl);
    showAlert('링크가 복사되었습니다');
  };

  const kakaoShare = () => {
    onClickShareFortune({
      domain: CLIENT_URL,
      path: '/fortune/' + referralCode?.referral_link || '',
    });
  };

  useEffect(() => {
    if (watchAllFields.shopName && watchAllFields.shopAddress) {
      setKeywordIsError(false);
      setKeywordErrorMessage('');
    }
  }, [watchAllFields.shopName, watchAllFields.shopAddress]);

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 pt-5 w-full sm:max-w-[375px] bg-white"
      >
        <InputContainer>
          <Label htmlFor="search_shop">
            <span>매장 선택</span>
            <LabelDescription className="text-[#F67C38] text-[12px]">
              *네이버 스마트플레이스 등록 매장 한정
            </LabelDescription>
          </Label>
          {/* 매장명 검색 인풋 */}
          <div className="mt-1 relative group">
            <Combobox value={selectedItem} onChange={handleChangeSelectItem} as={Fragment}>
              <ComboboxInput
                aria-label="네이버플레이스_매장명_검색"
                displayValue={(item: NaverSearchItem) => cleanupHtmlTags(item?.title || '')}
                onChange={handleChange}
                className="peer border outline-gray-6 active:outline-[#F9C5A8] focus-within:outline-[#F9C5A8] focus-within:bg-[#FFF3ED80] rounded-[14px] pl-14 pr-4 h-13 w-full placeholder:text-gray-5 transition-colors data-[error]:border-error"
                placeholder={'ex. 댓글몽샐러드 강남점'}
                ref={keywordRef}
                value={keyword}
                onBlur={handleBlur}
                id="search_shop"
                data-error={keywordIsError ? '' : undefined}
              />
              <TrackedComboboxOptions
                anchor={{
                  to: 'bottom start',
                  gap: 5,
                }}
                className={cn(
                  'rounded-[12px] overflow-hidden border border-gray-6 top-0 z-20 bg-white',
                  searchResult.length === 0 && !notFound && 'hidden',
                )}
              >
                <ScrollArea className="border-inherit w-[348px] max-w-[90vw] h-[300px] max-h-[70vh]">
                  {notFound && (
                    <div className="flex justify-center items-center h-32">
                      <p className="text-gray-3 text-[14px]">검색 결과가 없습니다</p>
                    </div>
                  )}
                  {searchResult.map((item, idx) => {
                    return (
                      <ComboboxOption
                        key={'headless' + item.title + idx}
                        className={cn(
                          `flex py-[14px] px-[16px] gap-2.5  self-stretch border-[#F2F2F2] empty:invisible`,
                          {
                            'border-b border-[#F2F2F2]': idx !== searchResult.length - 1, // 마지막 아이템이 아닌 경우에만 하단 테두리
                          },
                          'cursor-pointer hover:bg-[#F5F7FF]',
                          'data-[focus]:bg-[#F5F7FF]',
                        )}
                        value={item}
                      >
                        <div className="flex-col gap-1 flex w-full">
                          <p className="text-gray-3 text-[10px]">
                            {item.category.split('>').join(' > ')}
                          </p>
                          <div className="flex justify-between w-full items-center">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.title.replace(/<\/b>(?! )/g, '</b> '),
                              }}
                              className="text-gray-2 leading-normal text-[16px] *:text-[#EA5400] *:inline-block"
                            />
                          </div>

                          <p className="text-gray-3 text-[14px]">{item.roadAddress}</p>
                        </div>
                      </ComboboxOption>
                    );
                  })}
                </ScrollArea>
              </TrackedComboboxOptions>
            </Combobox>

            <input type="text" className="hidden" {...register('shopName')} />
            <input type="text" className="hidden" {...register('shopAddress')} />
            <input type="text" className="hidden" {...register('shopCategory')} />
            <div className="absolute left-4 top-1/2 -translate-y-1/2 peer-focus-within:text-primary peer-focus-within:*:text-primary text-gray-4">
              <SearchIcon className="transition-colors text-inherit" />
            </div>
          </div>
          <div className="relative">
            {keywordIsError && (
              <p className="mt-2 text-[12px] text-[#FF0000]">{keywordErrorMessage}</p>
            )}
          </div>

          <Label htmlFor="openedAt">
            <span>매장 오픈일 입력</span>
          </Label>
          <input
            {...register('openedAt', {
              required: '오픈일을 입력해주세요',
              pattern: {
                value: /\d{4}\/\d{2}\/\d{2}/,
                message: '올바른 날짜를 입력해주세요 (ex. 2000/12/20)',
              },
              maxLength: 12,
              minLength: 10,
            })}
            defaultValue={watchAllFields.openedAt}
            type="text"
            className="peer border outline-gray-6 active:outline-[#F9C5A8] focus-within:outline-[#F9C5A8] focus-within:bg-[#FFF3ED80] rounded-[14px] h-13 px-6 w-full placeholder:text-gray-5 transition-colors data-[error]:border-error"
            placeholder="YYYY/MM/DD"
            onChange={handleChangeOpenDate}
            id="openedAt"
            ref={openDateRef}
            inputMode="numeric"
            data-error={openDateIsError ? '' : undefined}
          />
          {openDateIsError && (
            <p className="mt-2 text-[12px] text-[#FF0000]">{openDateErrorMessage}</p>
          )}
        </InputContainer>

        <div className="flex flex-col gap-2 my-[25px] bg-[#F6F6F8] rounded-xl py-4 px-3 text-[15px]">
          <div className="flex gap-1">
            <Image src={'/img/fortune/eye_mong.png'} width={24} height={24} alt="eye_mong" />
            <p className="text-[#F67C38] font-semibold">이거 어디서 하는 건가요?</p>
          </div>
          <hr />

          <div className="flex gap-2.5 text-gray-1 my-4 justify-between">
            <div className="">
              <p className="text-[12px] font-semibold leading-tight">안녕하세요, 사장님 {':)'}</p>
              <p className="font-semibold leading-[1.4] mt-1">
                저희는 자영업자 사장님들을 위한 <br />
                AI 서비스를 만들고 있는 댓글몽이에요!
              </p>
            </div>
          </div>

          <p className="text-[12px] text-gray-2 leading-[1.4]">
            사장님께 힘이 되는 서비스를 제공해 드리고 싶어서, <br />
            을사년을 맞아 매장 운세를 무료로 봐드리는 이벤트를 열게 됐어요!
          </p>
        </div>
        <Button
          className="w-full gap-1.5 h-13 *:pointer-events-none"
          disabled={
            !watchAllFields.shopName ||
            !watchAllFields.shopAddress ||
            !watchAllFields.openedAt ||
            watchAllFields.openedAt.length < 10
          }
          data-gtm-id="fortune-form-submit"
        >
          <b>신년 운세</b> 확인하기
        </Button>
      </form>
      <div className="mt-10 pb-10">
        <div className="flex gap-4 justify-center text-center px-[23px]">
          <button
            className="flex flex-col gap-[15px] items-center justify-center w-[80px] *:pointer-events-none"
            onClick={kakaoShare}
            data-gtm-id="fortune-form-share-kakao"
          >
            <Image
              src={'/img/fortune/result/share-kakao.png'}
              width={50}
              height={50}
              alt="share-kakao"
              className="rounded-full mx-auto"
            />
            <p className="text-[#35373E] font-bold text-[14px]">카카오톡 공유</p>
          </button>

          <button
            className="flex flex-col gap-[10px] items-center justify-center w-[80px] *:pointer-events-none"
            onClick={handleClickCopy}
            data-gtm-id="fortune-form-share-link"
          >
            <Image
              src={'/img/fortune/result/share-link.png'}
              width={50}
              height={50}
              alt="share-link"
              className="rounded-full mx-auto"
            />
            <p className="text-[#35373E] font-bold text-[14px]">링크 공유</p>
          </button>
        </div>
      </div>
    </>
  );
};

const Label = twc.label`py-[13px] h-[50px] flex items-baseline font-semibold text-[#121212] gap-2`;
const LabelDescription = twc.span`text-[#F67C38] text-[12px]`;
const InputContainer = twc.div`flex flex-col gap-1 max-w-full`;

const SearchIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    <path
      d="M17 17L21 21M3 11C3 13.1217 3.84285 15.1566 5.34315 16.6569C6.84344 18.1571 8.87827 19 11 19C13.1217 19 15.1566 18.1571 16.6569 16.6569C18.1571 15.1566 19 13.1217 19 11C19 8.87827 18.1571 6.84344 16.6569 5.34315C15.1566 3.84285 13.1217 3 11 3C8.87827 3 6.84344 3.84285 5.34315 5.34315C3.84285 6.84344 3 8.87827 3 11Z"
      stroke={'currentColor'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default FortuneForm;
